import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { NgSelectModule } from '@ng-select/ng-select';

import { DataService } from '@/services/data.service';
import { StateService } from '@/services/state.service';

import { WeiToEthPipe } from '@/pipes/wei-to-eth.pipe';
import { FormatCashPipe } from '@/pipes/format-cash.pipe';

import { ChartComponent } from './chart/chart.component';

import { State } from '@/models/graph';

import { BehaviorSubject, combineLatest, filter, map, merge, mergeAll, Subject, switchMap, tap, distinctUntilChanged, takeLast } from 'rxjs';
import {environment} from "../../../../environments/environment";

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  selector: 'app-visual-data',
  imports: [
    CommonModule,
    NgSelectModule,
    FormsModule,
    
    ChartComponent,
    
    WeiToEthPipe,
    FormatCashPipe
  ],
  templateUrl: './visual-data.component.html',
  styleUrls: ['./visual-data.component.scss']
})

export class VisualDataComponent {
  
  statsData: any = { floor: 0, owners: 0, newOwners: 0, volume: 0 };

  isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  private statesData = new BehaviorSubject<State[]>([]);
  statesData$ = this.statesData.asObservable();

  constructor(
    public dataSvc: DataService,
    public stateSvc: StateService,
  ) {
    console.log('[VisualDataComponent] constructor called');
    
    combineLatest([
      this.stateSvc.activePeriod$,
      this.stateSvc.hideFlashLoans$
    ]).pipe(
      tap(([period, hideFlashLoans]) => console.log('[VisualDataComponent] combineLatest emitted:', { period, hideFlashLoans })),
      switchMap(([period, hideFlashLoans]) => this.dataSvc.statesData$.pipe(
        filter((states: State[]) => !!states?.length),
        // Only emit when we have the final merged data (check if we have recent data)
        filter((states: State[]) => {
          const now = Math.floor(Date.now() / 1000);
          const hasRecentData = states.some(state => Number(state.timestamp) > now - 86400); // Last 24h
          return hasRecentData;
        }),
        tap((states: State[]) => {
          console.log('[VisualDataComponent] statesData$ emitted:', states?.length, 'states');
          this.setStats(states, period);
        }),
        map((states: State[]) => this.filterFlashLoans(states, hideFlashLoans)),
        tap((states: State[]) => {
          console.log('[VisualDataComponent] Setting statesData:', states?.length, 'states');
          
          // Filter data by period before passing to chart
          const filteredStates = this.filterDataByPeriod(states, period);
          console.log('[VisualDataComponent] Filtered for chart:', filteredStates?.length, 'states for period:', period);
          
          this.statesData.next(filteredStates);
        }),
      )),
      takeUntilDestroyed()
    ).subscribe(() => {
      console.log('[VisualDataComponent] combineLatest subscription completed');
    });
  }

  setAcivePeriod(period: any): void {
    this.stateSvc.setActivePeriod(period);
  }

  filterFlashLoans(states: State[], hideFlashLoans: boolean): State[] {
    if (!hideFlashLoans || environment.collection === 'cryptophunks') return states;
    const flashLoans = ['2021-10-28', '2024-10-3'];
    return states.filter((state) => !flashLoans.includes(state.id));
  }

  filterDataByPeriod(states: State[], period: any): State[] {
    if (!states?.length || !period?.limit) return states;
    
    // Take the first n entries (most recent data, since data is sorted newest first)
    const filteredStates = states.slice(0, period.limit);
    console.log('[VisualDataComponent] filterDataByPeriod:', {
      totalStates: states.length,
      periodLimit: period.limit,
      filteredCount: filteredStates.length,
      firstDate: filteredStates[0]?.id,
      lastDate: filteredStates[filteredStates.length - 1]?.id
    });
    
    return filteredStates;
  }

  setStats(states: State[], period: any) {
    const n = period?.limit || 0;
    const firstNEntries = states.slice(0, n);
    // console.log('firstNEntries', firstNEntries);
    this.statsData = {
      floor: states[0].floor,
      owners: Number(firstNEntries[0].owners),
      newOwners: (Number(firstNEntries[0].owners) || 0) - (Number(firstNEntries[firstNEntries.length - 1].owners) || 0),
      volume: firstNEntries.reduce((a, b) => BigInt(a) + BigInt(b.volume), BigInt(0))
    };
  }

    protected readonly environment = environment;
}
